import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import ListItem from "../../../Admin/Files/ListItem";
import LessonItem from "../../Tracks/LessonItem";
import { fetchIndices } from "../../../Admin/Indices/actions";
import { getIndeices, setIndeices } from "../../../../context/sessionStorage";

const groupItemsByPath = (items) => {
    const groups = {};
    items.forEach(item => {
        const path = item.IndexPath || item.indexPath || "";
        if (!groups[path]) {
            groups[path] = [];
        }
        groups[path].push(item);
    });
    return groups;
};

const RelativeLessonsPane = React.memo(({ relativeLinks, relativeLessons, user, t, loading }) => {
    const dispatch = useDispatch();
    const indices = useSelector(state => state.indices);

    useEffect(() => {
        const cachedIndices = getIndeices();
        if (cachedIndices === null || cachedIndices.length === 0) {
            dispatch(fetchIndices());
        }
    }, [dispatch]);

    useEffect(() => {
        if (indices) {
            setIndeices(indices);
        }
    }, [indices]);

    const groupedLessons = useMemo(() => {
        return relativeLessons && Array.isArray(relativeLessons) ? groupItemsByPath(relativeLessons) : {};
    }, [relativeLessons]);

    const groupedLinks = useMemo(() => {
        return relativeLinks && Array.isArray(relativeLinks) ? groupItemsByPath(relativeLinks) : {};
    }, [relativeLinks]);

    const renderGroupedLessons = () => {
        return Object.keys(groupedLessons).map((path, groupIdx) => {
            const items = groupedLessons[path];
            return (
                <div key={`group-${groupIdx}`} className="relative-path-group mb-4 animate__animated animate__fadeIn">
                    <h5 className="relative-group-path-title">
                        <i className="fal fa-folder-open ml-2"></i>
                        <span>{path || t("Pages.Chatbot.GeneralRelative")}</span>
                        <span className="group-count-badge mr-2">{items.length}</span>
                    </h5>
                    <div className="relative-items-list row px-2">
                        {items.map((lesson, idx) => {
                            const type = lesson.Type || lesson.type || "";
                            if (type === "File") {
                                const fileItem = {
                                    Id: lesson.Id || lesson.id,
                                    Name: lesson.Name || lesson.name,
                                    Icon: lesson.Icon || lesson.icon,
                                    IsPublic: true,
                                    IsActive: true,
                                    isFavorite: false,
                                    isRated: true,
                                    RateCount: lesson.RateCount || 0,
                                    Stars: lesson.Stars || 0,
                                    CreationDate: lesson.CreationDate || new Date().toISOString(),
                                    token: lesson.Token || lesson.token,
                                    ViewCount: lesson.ViewCount || 0,
                                    Index: lesson.Index ? { Id: lesson.Index.Id, Name: lesson.Index.Name, Type: "Files" } : { Id: lesson.IndexId, Type: "Files" },
                                    Lessons: [],
                                    extension: (lesson.Name && lesson.Name.includes(".")) ? "." + lesson.Name.split(".").pop() : "",
                                    Bio: lesson.Bio || "",
                                    IsView: true,
                                    IsForTeacher: false,
                                };
                                return (
                                    <div key={`file-${fileItem.Id}-${idx}`} className="relative-item-wrapper col-12 mb-3">
                                        <ListItem
                                            item={fileItem}
                                            user={user}
                                            IsAdmin={false}
                                            isFromHome={false}
                                            isSmall={false}
                                        />
                                    </div>
                                );
                            } else {
                                const lessonItem = {
                                    Id: lesson.Id || lesson.id,
                                    Name: lesson.Name || lesson.name,
                                    IsPublic: true,
                                    IsActive: true,
                                    Icon: lesson.Icon || lesson.icon,
                                    isFavorite: false,
                                    RateCount: lesson.RateCount || 0,
                                    Stars: lesson.Stars || 0,
                                    CreationDate: lesson.CreationDate || new Date().toISOString(),
                                    UpdateDate: lesson.UpdateDate || new Date().toISOString(),
                                    ViewCount: lesson.ViewCount || 0,
                                    Index: lesson.Index ? { Id: lesson.Index.Id, Name: lesson.Index.Name, Type: type === "Game" ? "Games" : (type === "Exam" ? "Exams" : "Lessons") } : {
                                        Id: lesson.IndexId,
                                        Type: type === "Game" ? "Games" : (type === "Exam" ? "Exams" : "Lessons")
                                    },
                                    Bio: lesson.Bio || "",
                                    TotalMark: null,
                                    Mark: null,
                                    UserTime: null,
                                    Url: null,
                                    User: lesson.User ? { Id: lesson.User.Id, Name: lesson.User.Name } : { Name: "" },
                                    IsView: true,
                                };
                                const lessonType = type === "Game" ? "Games" : (type === "Exam" ? "Exams" : "Lessons");
                                return (
                                    <div key={`lesson-${lessonItem.Id}-${idx}`} className="relative-item-wrapper col-12 mb-3">
                                        <LessonItem
                                            lesson={lessonItem}
                                            type={lessonType}
                                            user={user}
                                            IsAdmin={false}
                                            isFromHome={false}
                                            isSmall={false}
                                        />
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>
            );
        });
    };

    const renderGroupedLinks = () => {
        return Object.keys(groupedLinks).map((path, groupIdx) => {
            const links = groupedLinks[path];
            return (
                <div key={`link-group-${groupIdx}`} className="relative-path-group mb-4 animate__animated animate__fadeIn">
                    <h5 className="relative-group-path-title">
                        <i className="fal fa-folder-open ml-2"></i>
                        <span>{path || t("Pages.Chatbot.GeneralRelative")}</span>
                        <span className="group-count-badge mr-2">{links.length}</span>
                    </h5>
                    <div className="relative-lessons-grid">
                        {links.map((link, idx) => {
                            const isImgIcon = link.icon && (link.icon.startsWith("http") || link.icon.startsWith("/") || link.icon.includes("."));
                            return (
                                <a
                                    key={idx}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="relative-lesson-card"
                                >
                                    {isImgIcon ? (
                                        <img
                                            src={link.icon}
                                            alt=""
                                            className="relative-lesson-img-icon"
                                            style={{ width: "24px", height: "24px", objectFit: "contain", flexShrink: 0, borderRadius: "4px" }}
                                        />
                                    ) : (
                                        <i className={link.icon ? (link.icon.startsWith("fa") ? link.icon : `fal ${link.icon}`) : (link.type === "File" ? "fal fa-file-alt" : "fal fa-graduation-cap")}></i>
                                    )}
                                    <div className="relative-lesson-info">
                                        <span className="relative-lesson-name">{link.text}</span>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                </div>
            );
        });
    };

    if (loading) {
        return (
            <div className="relative-lessons-pane-body text-center py-5 animate__animated animate__fadeIn">
                <div className="relative-lessons-spinner mb-3">
                    <div className="double-bounce1"></div>
                    <div className="double-bounce2"></div>
                </div>
                <p className="text-muted" style={{ fontWeight: 600 }}>
                    {t("Pages.Chatbot.LoadingRelativeLessons")}
                </p>
            </div>
        );
    }

    const hasLessons = relativeLessons && Array.isArray(relativeLessons) && relativeLessons.length > 0;
    const hasLinks = relativeLinks && relativeLinks.length > 0;

    if (!hasLessons && !hasLinks) {
        return (
            <div className="relative-lessons-pane-body text-center text-muted py-5 animate__animated animate__fadeIn">
                <i className="fal fa-link empty-icon d-block mb-3 h2 text-muted"></i>
                <p style={{ fontWeight: 600 }}>
                    {t("Pages.Chatbot.NoRelativeLessons")}
                </p>
            </div>
        );
    }

    if (hasLessons) {
        return (
            <div className="relative-lessons-pane-body">
                <div className="relative-lessons-section p-3">
                    {renderGroupedLessons()}
                </div>
            </div>
        );
    }

    return (
        <div className="relative-lessons-pane-body">
            <div className="relative-lessons-section animate__animated animate__fadeIn p-3">
                <h4 className="relative-lessons-title mb-3" style={{ fontWeight: 700 }}>
                    <i className="fal fa-book-reader ml-2" style={{ color: "#d4af37" }}></i>
                    {t("Pages.Chatbot.RelativeLessons")}
                </h4>
                {renderGroupedLinks()}
            </div>
        </div>
    );
});

export default RelativeLessonsPane;
